import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Drawer, Group, Stack, Switch, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { isAxiosError } from 'axios';
import { IconLock } from '@tabler/icons-react';
import type { AccountDto } from './types';
import { useCreateAccount, useUpdateAccount } from './accountsApi';

// Two modes:
//  - create: `parent` is set — the new account lands under it, inheriting
//    type/normal-balance server-side.
//  - edit: `account` is set — rename/recode/(de)activate. System accounts
//    lock everything except the active toggle (mirrors the API rule).
interface Props {
  opened: boolean;
  onClose: () => void;
  parent: AccountDto | null;
  account: AccountDto | null;
}

interface FormValues {
  code: string;
  nameAr: string;
  nameEn: string;
  currencyCode: string;
  isActive: boolean;
}

export function AccountFormDrawer({ opened, onClose, parent, account }: Props) {
  const { t, i18n } = useTranslation();
  const create = useCreateAccount();
  const update = useUpdateAccount();
  const editing = account !== null;
  const isSystem = account?.isSystem ?? false;
  const submitting = create.isPending || update.isPending;
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const form = useForm<FormValues>({
    initialValues: { code: '', nameAr: '', nameEn: '', currencyCode: '', isActive: true },
    validate: {
      code: (v) => (v.trim() ? null : t('common.errors.required')),
      nameAr: (v) => (v.trim() ? null : t('common.errors.required')),
    },
  });

  useEffect(() => {
    if (!opened) return;
    form.setValues({
      code: account?.code ?? '',
      nameAr: account?.nameAr ?? '',
      nameEn: account?.nameEn ?? '',
      currencyCode: account?.currencyCode ?? parent?.currencyCode ?? 'ILS',
      isActive: account?.isActive ?? true,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, account, parent]);

  const submit = async (values: FormValues) => {
    try {
      if (editing) {
        await update.mutateAsync({
          id: account.accountId,
          request: {
            code: values.code.trim(),
            nameAr: values.nameAr.trim(),
            nameEn: values.nameEn.trim() || null,
            isActive: values.isActive,
          },
        });
      } else {
        await create.mutateAsync({
          code: values.code.trim(),
          nameAr: values.nameAr.trim(),
          nameEn: values.nameEn.trim() || null,
          parentAccountId: parent!.accountId,
          currencyCode: values.currencyCode.trim() || null,
        });
      }
      notifications.show({
        color: 'green',
        title: editing ? t('common.toasts.updated') : t('common.toasts.created'),
        message: values.nameAr,
      });
      onClose();
    } catch (err) {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: extractApiMessage(err) ?? t('common.toasts.errorBody'),
      });
    }
  };

  const parentName =
    parent === null ? '' : i18n.language === 'ar' ? parent.nameAr : (parent.nameEn ?? parent.nameAr);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={editing ? t('accounting.chart.form.editTitle') : t('accounting.chart.form.createTitle')}
      position={drawerPosition}
      size="lg"
      padding="lg"
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack gap="md">
          {!editing && parent !== null && (
            <Text size="sm" c="dimmed">
              {t('accounting.chart.form.underParent', { parent: `${parent.code} — ${parentName}` })}
            </Text>
          )}
          {isSystem && (
            <Alert color="gray" icon={<IconLock size={16} stroke={1.6} />}>
              {t('accounting.chart.form.systemLocked')}
            </Alert>
          )}
          <TextInput
            label={t('accounting.chart.form.codeLabel')}
            description={t('accounting.chart.form.codeHelp')}
            withAsterisk
            disabled={isSystem}
            className="font-numeric"
            {...form.getInputProps('code')}
          />
          <TextInput
            label={t('accounting.chart.form.nameArLabel')}
            withAsterisk
            disabled={isSystem}
            {...form.getInputProps('nameAr')}
          />
          <TextInput
            label={t('accounting.chart.form.nameEnLabel')}
            disabled={isSystem}
            {...form.getInputProps('nameEn')}
          />
          {!editing && (
            <TextInput
              label={t('accounting.chart.form.currencyLabel')}
              description={t('accounting.chart.form.currencyHelp')}
              maxLength={3}
              {...form.getInputProps('currencyCode')}
            />
          )}
          {editing && (
            <Switch
              label={t('accounting.chart.form.activeLabel')}
              description={t('accounting.chart.form.activeHelp')}
              checked={form.values.isActive}
              onChange={(event) => form.setFieldValue('isActive', event.currentTarget.checked)}
            />
          )}

          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={submitting}>
              {editing ? t('common.save') : t('common.create')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  );
}

function extractApiMessage(err: unknown): string | null {
  if (isAxiosError(err)) {
    const data = err.response?.data as { detail?: string; title?: string } | undefined;
    return data?.detail ?? data?.title ?? err.message;
  }
  return null;
}
